// The row you just decided on should answer you, then leave.
//
// Every Review Center tab and the issues list used to do one of two things
// after an approve / reject / resolve / abandon: refetch the whole list (so
// the row sat there unchanged until the round-trip came back) or drop the
// row in the same frame (so it vanished before you had read which one you
// had just acted on). Neither is a good answer to "did that work?".
//
// The answer lives in REDUX, not in the component: `state.issues.settled`
// holds, per list scope, the ids the server has just confirmed as decided
// and the outcome to show on each. A list dispatches `rowSettled(scope,
// id, label)` after the server says yes and nothing else; this hook reads
// the store and tells the list how to draw:
//
//   const settled = useSettledRows("revisions");
//   ...on success:   dispatch(rowSettled("revisions", row.commitId, "Approved"));
//   ...rendering:    const rows = settled.apply(visibleRows, (r) => r.commitId);
//                    row._settled  -> the badge text, while the row is in its beat
//   ...on a row:     <SettledBadge row={r} />   style={settledStyle(r)}
//
// Because the fact is in the store, two lists showing the same row (a
// proposal on the Review Center and its issue on the issues page) agree
// on it, a refetch cannot un-say it, and a row that turns out to still be
// open on the server is put back with `rowUnsettled`.
//
// The beat: a row stays badged for SETTLE_MS and is then dropped. One
// timer per hook - not per row - dispatches a sweep that removes the
// expired records from the store; until then `apply` hides them by clock.
import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tag } from "antd";
import { SETTLE_MS } from "reducers/issues";
import { sweepSettledRows } from "actions/issues";

export { SETTLE_MS };

const EMPTY = {};

const TONE = {
  Approved: { color: "#389e0d", bg: "#f6ffed", bd: "#b7eb8f" },
  Resolved: { color: "#389e0d", bg: "#f6ffed", bd: "#b7eb8f" },
  Applied: { color: "#389e0d", bg: "#f6ffed", bd: "#b7eb8f" },
  Accepted: { color: "#389e0d", bg: "#f6ffed", bd: "#b7eb8f" },
  Completed: { color: "#389e0d", bg: "#f6ffed", bd: "#b7eb8f" },
  "Sent back": { color: "#d4380d", bg: "#fff2e8", bd: "#ffbb96" },
  Rejected: { color: "#d4380d", bg: "#fff2e8", bd: "#ffbb96" },
  Abandoned: { color: "#595959", bg: "#fafafa", bd: "#d9d9d9" },
  Removed: { color: "#595959", bg: "#fafafa", bd: "#d9d9d9" },
};

export function useSettledRows(scope) {
  const dispatch = useDispatch();
  const marks = useSelector((s) => (s.issues && s.issues.settled && s.issues.settled[scope]) || EMPTY);
  const timer = useRef(null);

  // Whenever this scope holds a live record, arrange ONE sweep for the
  // moment the newest one expires. The sweep re-renders every subscriber,
  // which is what makes the expired rows drop out of `apply`.
  useEffect(() => {
    clearTimeout(timer.current);
    const ats = Object.values(marks).map((m) => m.at);
    if (!ats.length) return undefined;
    const due = Math.max(...ats) + SETTLE_MS - Date.now() + 20;
    timer.current = setTimeout(() => dispatch(sweepSettledRows(Date.now())), Math.max(0, due));
    return () => clearTimeout(timer.current);
  }, [marks, dispatch]);

  const apply = useCallback((rows, idOf = (r) => r && r._id) => {
    if (!rows || !rows.length) return rows || [];
    if (!Object.keys(marks).length) return rows;
    const now = Date.now();
    const out = [];
    for (const r of rows) {
      const key = String(idOf(r) || "");
      const m = key && marks[key];
      if (!m) { out.push(r); continue; }
      if (now - m.at >= SETTLE_MS) continue;           // beat over: gone
      out.push({ ...r, _settled: m.label });           // in its beat: badged
    }
    return out;
  }, [marks]);

  const isSettled = useCallback((id) => !!marks[String(id)], [marks]);

  return { apply, isSettled, marks };
}

// The badge itself. Renders nothing until the row has been settled.
export function SettledBadge({ row, style }) {
  const label = row && row._settled;
  if (!label) return null;
  const t = TONE[label] || TONE.Approved;
  return (
    <Tag style={{ color: t.color, background: t.bg, borderColor: t.bd, fontWeight: 700, marginLeft: 8, ...style }}>
      ✓ {label}
    </Tag>
  );
}

// A row in its beat is dimmed, so the eye reads "on its way out".
export const settledStyle = (row) => (row && row._settled
  ? { opacity: 0.55, transition: "opacity .3s ease" }
  : undefined);

export default useSettledRows;
